import isEqual from 'lodash/isEqual';
import { match, P } from 'ts-pattern';
import * as z from 'zod';
import type { RequestContainer } from '$lib/container/container.server';
import { equalOrIsNull } from '$lib/kysely/kysely-utils.js';
import type { PartialOnUndefined } from '$lib/utils/types.js';
import type { FollowableInput } from '$gql/resolver-types';
import type {
    MutationToggleFollowingArgs,
    QueryFollowableArgs,
} from '$gql/resolver-types.js';
import type { CompanyRef } from '../CompanyRef';
import type { UserRef } from '../UserRef';

export type FollowableRef = UserRef | CompanyRef;

export const zFollowableInput = z.object({
    userId: z.number().nullish(),
    companyId: z.number().nullish(),
});

export function matchFollowableInput<A, B = A>(
    p: PartialOnUndefined<FollowableInput>,
    callbacks: { user: (id: number) => A; company: (id: number) => B },
): A | B {
    return match(p)
        .with({ userId: P.number }, ({ userId }) => callbacks.user(userId))
        .with({ companyId: P.number }, ({ companyId }) => callbacks.company(companyId))
        .run();
}

export function followableInputAreEqual(
    p1: FollowableInput,
    p2: FollowableInput,
): boolean {
    const normalize = (p: FollowableInput) => ({
        companyId: p.companyId ?? null,
        userId: p.userId ?? null,
    });

    return isEqual(normalize(p1), normalize(p2));
}

export function followableInputToRef(
    p: PartialOnUndefined<FollowableInput>,
    context: Pick<RequestContainer, 'refs'>,
): FollowableRef {
    return matchFollowableInput(p, {
        user: (id) => context.refs().user(id),
        company: (id) => context.refs().company(id),
    });
}

export class QueryFollowableRef {
    constructor(private readonly _context: Pick<RequestContainer, 'refs'>) {}

    async followable(this: this, args: PartialOnUndefined<QueryFollowableArgs>) {
        const { refs } = this._context;
        return match(args.id)
            .with({ userId: P.number }, ({ userId }) => refs().user(userId))
            .with({ companyId: P.number }, ({ companyId }) => refs().company(companyId))
            .otherwise(() => {
                throw new Error('Followable is invalid.');
            });
    }
}

export async function toggleFollowingMutation(
    args: PartialOnUndefined<MutationToggleFollowingArgs>,
    context: Pick<
        RequestContainer,
        'db' | 'securityManager' | 'refs' | 'notificationUtils' | 'dataLoaderRegistry'
    >,
) {
    const { db, securityManager, dataLoaderRegistry, refs, notificationUtils } = context;
    const userId = await securityManager().currentUserIdOrNull();

    if (userId == null) {
        return followableInputToRef(args.target, context);
    }

    let created = await db()
        .transaction()
        .execute(async (trx) => {
            let follow = await trx
                .selectFrom('following')
                .selectAll()
                .where('author_id', '=', userId)
                .where(({ and, ref }) =>
                    and([
                        equalOrIsNull(ref('target_user_id'), args.target.userId),
                        equalOrIsNull(ref('target_company_id'), args.target.companyId),
                    ]),
                )
                .executeTakeFirst();

            if (follow != null) {
                await trx
                    .deleteFrom('following')
                    .where('id', '=', follow.id)
                    .executeTakeFirst();
                return false;
            } else {
                await trx
                    .insertInto('following')
                    .values({
                        target_company_id: args.target.companyId,
                        target_user_id: args.target.userId,
                        author_id: userId,
                        date: new Date(),
                    })
                    .executeTakeFirst();
                return true;
            }
        });

    dataLoaderRegistry().clearAll();
    const target = followableInputToRef(args.target, context);
    const follower = refs().user(userId);

    if (created) {
        await notificationUtils().createForNewFollower({
            recipient: target,
            follower: follower,
        });
    }

    return followableInputToRef(args.target, context);
}
